import styled from "styled-components";
import dayjs from "dayjs";
import moment from "moment-timezone";
import { useState, useEffect } from "react";
import { Modal } from "antd";
import { IconCloseSvg } from "@/components/svgs";
import { FormSendInquiry } from "./FormSendInquiry";
import { CardSendInquirySucess } from "./CardSendInquirySucess";
import { createCharterBooking } from "@/stores/apis/apiCharter";
import { readTrafficSource } from "@/utils/trafficSource";
import { toast } from "@/modules/toast";
import { DATETIME_FORMAT_STANDARD, NOTIFICATION_TYPE } from "@/config/constants";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import { selectAuthState, selectFormatDateTimeFull } from "@/stores/slices/authSlice";

export function ModalEmptyLegSendInquiry({ visible = false, onClose, booking = {}, onSendSuccess }) {
	const { user } = useSelector(selectAuthState);
	const formatDTFull = useSelector(selectFormatDateTimeFull);
	const [sendSuccess, setSendSuccess] = useState(undefined);
	const [formSendValues, setFormSendValues] = useState({
		additionalNote: "",
		additionalInfo: {
			is_bring_pet: false,
			is_bring_oversize_luggage: false,
		},
		departureDatetime: "",
		allowInputDepartureDatetime: false,
		passengers: 1,
	});
	const [error, setError] = useState({});
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (visible && booking && booking._id) {
			// check allow input departure datetime
			let allowInput = false;
			if (!booking?.departure && !booking?.departure_window) {
				allowInput = true;
			} else if (booking?.departure_window) {
				allowInput = true;
			}

			// format departure datetime to airport timezone
			let value = "";
			if (booking?.departure?.utc_datetime && booking?.from_airport?.location?.timezone) {
				value = dayjs(
					moment(booking?.departure?.utc_datetime)
						.tz(booking?.from_airport?.location?.timezone)
						.format(DATETIME_FORMAT_STANDARD),
				).toISOString();
			}
			setFormSendValues({
				...formSendValues,
				allowInputDepartureDatetime: allowInput,
				departureDatetime: value,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [booking, visible]);

	const validateFromInquiry = () => {
		let valid = true;

		let errs = {};
		if (!formSendValues.departureDatetime) {
			errs.departureDatetime = "Please select departure time.";
		}
		if (!formSendValues.passengers || formSendValues.passengers <= 0) {
			errs.passengers = "Please enter passengers.";
		}
		// check have departure datetime between departure window and time TBD
		if (booking?.departure_window && formSendValues.departureDatetime) {
			let timezone = booking?.from_airport?.location?.timezone;
			let departure_datetime = formSendValues.departureDatetime;
			let value = dayjs(departure_datetime).format(DATETIME_FORMAT_STANDARD);
			departure_datetime = dayjs.tz(value, timezone).toISOString();
			if (
				moment(booking?.departure?.utc_datetime).isAfter(moment(departure_datetime)) ||
				moment(booking?.departure_window?.utc_datetime).isBefore(moment(departure_datetime))
			) {
				errs.departureDatetime = `Please select a valid departure time (From: ${moment(
					booking?.departure?.utc_datetime,
				)
					.tz(timezone)
					.format(formatDTFull)} - To: ${moment(booking?.departure_window?.utc_datetime)
					.tz(timezone)
					.format(formatDTFull)}).`;
			}
		} else if (
			!booking?.departure?.utc_datetime &&
			booking?.empty_leg?.departure?.utc_datetime &&
			formSendValues.departureDatetime
		) {
			let timezone = booking?.from_airport?.location?.timezone;
			let departure_datetime = formSendValues.departureDatetime;
			let value = dayjs(departure_datetime).format(DATETIME_FORMAT_STANDARD);
			departure_datetime = dayjs.tz(value, timezone).toISOString();
			if (
				booking?.empty_leg?.departure_window?.utc_datetime &&
				(moment(booking?.empty_leg?.departure?.utc_datetime).isAfter(moment(departure_datetime)) ||
					moment(booking?.empty_leg?.departure_window?.utc_datetime).isBefore(moment(departure_datetime)))
			) {
				errs.departureDatetime = `Please select a valid departure time (From: ${moment(
					booking?.empty_leg?.departure?.utc_datetime,
				)
					.tz(timezone)
					.format(formatDTFull)} - To: ${moment(booking?.empty_leg?.departure_window?.utc_datetime)
					.tz(timezone)
					.format(formatDTFull)}).`;
			} else if (moment(booking?.empty_leg?.departure?.utc_datetime).isAfter(moment(departure_datetime))) {
				errs.departureDatetime = `Please select a valid departure time (From: ${moment(
					booking?.empty_leg?.departure?.utc_datetime,
				)
					.tz(timezone)
					.format(formatDTFull)}).`;
			}
		}

		if (!isEmpty(errs)) {
			valid = false;
			setError(errs);
		}

		return valid;
	};

	const onSubmitSendInquiry = async () => {
		if (!user?.activated) {
			setError({
				submit_failed: "You must verify your account before taking any further action",
			});
			return;
		}

		const valid = validateFromInquiry();
		if (!valid) return;

		setLoading(true);

		let empty_leg_type = "DEFAULT";
		switch (booking.label) {
			case "INVITED":
				if (booking.is_automatic) {
					empty_leg_type = "AUTO_INVITE";
				} else {
					empty_leg_type = "MANUAL_INVITE";
				}
				break;
			case "MATCH":
				empty_leg_type = "MATCH";
				break;
			default:
				break;
		}

		// format time range with timezone
		let timezone = booking?.from_airport?.location?.timezone;
		let departure_datetime = formSendValues?.departureDatetime;
		if (timezone) {
			let value = dayjs(departure_datetime).format(DATETIME_FORMAT_STANDARD);
			departure_datetime = dayjs.tz(value, timezone).toISOString();
		}

		let payload = {
			type: "EMPTY_LEG",
			empty_leg_type: empty_leg_type,
			legs: [
				{
					from_airport: booking?.from_airport?._id,
					to_airport: booking?.to_airport?._id,
					departure_datetime: departure_datetime,
					passengers: formSendValues.passengers,
				},
			],
			aircraft_categories: [],
			note: formSendValues?.additionalNote,
			additional_info: {
				is_bring_pet: formSendValues?.additionalInfo.is_bring_pet,
				is_bring_oversize_luggage: formSendValues?.additionalInfo.is_bring_oversize_luggage,
			},
			empty_leg: booking.label !== "INVITED" ? booking?._id : null,
			empty_leg_invited: booking.label === "INVITED" ? booking?._id : null,
		};
		const traffic_source = readTrafficSource();
		const responseSend = await createCharterBooking({
			...payload,
			traffic_source,
		});
		if (responseSend?.success) {
			setSendSuccess(true);
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: "Sending of Empty Leg Inquiry Failed.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 300);
	};
	return (
		<StyledModal
			centered
			closeIcon={<IconCloseSvg />}
			open={visible}
			onCancel={() => {
				if (sendSuccess) {
					onSendSuccess?.();
					setFormSendValues({
						additionalNote: "",
						additionalInfo: {
							is_bring_pet: false,
							is_bring_oversize_luggage: false,
						},
						departureDatetime: "",
						passengers: 1,
					});
					setSendSuccess(false);
				}
				onClose();
			}}
			footer={null}
			width="720px"
			wrapClassName="auth-wrapper"
		>
			<Container>
				{sendSuccess === false || sendSuccess === undefined ? (
					<FormSendInquiry
						data={formSendValues}
						booking={booking}
						onChange={(vals) =>
							setFormSendValues({
								...formSendValues,
								...vals,
							})
						}
						onSubmit={onSubmitSendInquiry}
						error={error}
						loading={loading}
					/>
				) : (
					<CardSendInquirySucess data={{ ...booking, ...formSendValues }} />
				)}
			</Container>
		</StyledModal>
	);
}

const StyledModal = styled(Modal)`
	.ant-modal-content {
		background-color: #ffffff;
		border-radius: 24px;
		box-shadow: none;
	}
	.ant-modal-close {
		width: 28px;
		height: 28px;
		top: 22px;
		right: 22px;
		@media (max-width: 576px) {
			top: 22px;
			right: 4px;
		}
	}
	.ant-modal-body {
		overflow-y: auto;
		overflow-x: hidden;
		max-height: 80vh;
	}
`;
const Container = styled.div`
	width: "100%";
	padding: clamp(24px, 8vw, 60px) clamp(16px, 4vw, 60px);
`;
